/** Single source of truth for job filtering + sorting.
 *
 * Replaces the three duplicated predicates that lived in the legacy
 * index.html (applyFilters, nextPage, exportFilteredJobsToCSV).
 */

import type { Filters, Job } from "./types";
import { getJobStates } from "./usStates";

export function getFilteredJobs(allJobs: Job[], filters: Filters): Job[] {
  const q = filters.q.trim().toLowerCase();
  const out: Job[] = [];

  for (const job of allJobs) {
    if (!filters.showDuplicates && job.is_duplicate === 1) continue;
    if (filters.platform !== "all" && job.ats !== filters.platform) continue;
    if (filters.company && job.company !== filters.company) continue;
    if (filters.profile && job.profile !== filters.profile) continue;
    if (filters.tag && !(job.tags || []).includes(filters.tag)) continue;
    if (filters.state) {
      const jobStates = getJobStates(job.location);
      if (!jobStates.includes(filters.state)) continue;
    }
    if (q) {
      const hay = `${job.title} ${job.company} ${job.location}`.toLowerCase();
      if (!hay.includes(q)) continue;
    }
    out.push(job);
  }

  sortJobs(out, filters.sort);
  return out;
}

export function sortJobs(jobs: Job[], sort: Filters["sort"]): void {
  switch (sort) {
    case "newest":
      jobs.sort((a, b) => b.added_at.localeCompare(a.added_at));
      break;
    case "company":
      jobs.sort((a, b) => a.company.localeCompare(b.company));
      break;
    case "score":
      jobs.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return b.added_at.localeCompare(a.added_at);
      });
      break;
  }
}

export function paginate<T>(items: T[], page: number, perPage: number): { slice: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;
  return { slice: items.slice(start, start + perPage), totalPages };
}
